"use client";

import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { type PropsWithChildren, Suspense, useEffect } from "react";

export function AnalyticsProvider({ children }: PropsWithChildren) {
	useEffect(() => {
		if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;

		posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
			api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
			capture_pageview: false,
			capture_pageleave: true,
			opt_out_capturing_by_default: true,
			opt_out_persistence_by_default: true,
			persistence: "localStorage+cookie",
		});

		function syncWithConsent() {
			const termly = window.Termly;
			const allowed = !!termly?.getConsentState?.()?.analytics;
			if (allowed) {
				posthog.opt_in_capturing();
			} else {
				posthog.opt_out_capturing();
			}
		}

		function attachListeners(): boolean {
			const termly = window.Termly;
			if (termly && typeof termly.on === "function") {
				termly.on("initialized", syncWithConsent);
				termly.on("consent", syncWithConsent);
				syncWithConsent();
				return true;
			}
			return false;
		}

		if (!attachListeners()) {
			const interval = setInterval(() => {
				if (attachListeners()) {
					clearInterval(interval);
				}
			}, 500);
			return () => clearInterval(interval);
		}
	}, []);

	return (
		<PostHogProvider client={posthog}>
			<Suspense fallback={null}>
				<PageView />
			</Suspense>
			{children}
		</PostHogProvider>
	);
}

function PageView() {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const posthog = usePostHog();

	useEffect(() => {
		if (pathname && posthog) {
			let url = window.origin + pathname;
			if (searchParams.toString()) {
				url = `${url}?${searchParams.toString()}`;
			}

			posthog.capture("$pageview", { $current_url: url });
		}
	}, [pathname, searchParams, posthog]);

	return null;
}
